import { ofetch } from 'ofetch';

import { createMicroCMSProxyClient } from '@/wabunka-src/common/core/data/microCMSProxyClient';
import type { EnExperienceImageContent } from '@/wabunka-src/features/Top/models/experientialStay';

// microCMS の 1 リクエスト上限
const MICRO_CMS_MAX_LIMIT = 100;

/**
 * microCMS `en-experiences` から、指定スラグ（= コンテンツ id）のヒーロー画像のみを一括取得する。
 *
 * Nuxt/Vue のコンテキストに依存しない純粋なデータ取得関数（Data Layer）。`useAsyncData` /
 * `useRuntimeConfig` / `inject` などのコンテキスト依存 API はここでは使わない。これにより、呼び出し側
 * composable が「await の後にコンテキスト API を呼ばない」制約を構造として表現する
 * （await 後にコンテキスト API を呼ぶと SSR・hydration でコンテキストを失い setup がクラッシュするため）。
 *
 * microCMS の API キーはサーバープロキシ側にあるため、ここでは相対パスを叩く（クライアント専用）。
 */
export const fetchEnExperienceHeroImages = async (slugs: string[]): Promise<EnExperienceImageContent[]> => {
  if (slugs.length === 0) return [];

  // microCMS は 1 リクエスト最大 100 件のため、スラグが 100 件を超える場合は分割して全件取得する。
  // 単発リクエストで `limit` を 100 に丸めると 101 件目以降の画像がサイレントに欠落し、
  // 該当カードが（画像突合できず）非表示になるため、件数に依らず全件取得できるようチャンク化する。
  const client = createMicroCMSProxyClient(ofetch);

  const contents: EnExperienceImageContent[] = [];
  for (let offset = 0; offset < slugs.length; offset += MICRO_CMS_MAX_LIMIT) {
    const chunk = slugs.slice(offset, offset + MICRO_CMS_MAX_LIMIT);
    const res = await client.getList<EnExperienceImageContent>({
      endpoint: 'en-experiences',
      queries: {
        ids: chunk,
        fields: 'id,pcMainImage,spMainImage,subImages',
        limit: chunk.length,
      },
    });
    contents.push(...res.contents);
  }
  return contents;
};
